import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Tags } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { toggleTag, setTagMethod } from '../../../../redux/actions/search.action';
import {
    selectTags,
    selectSelectedTags,
    selectTagMethodOr,
    selectDisplayTags
} from '../../../../redux/selectors/search.selectors';

const TagFilters = () => {
    const dispatch = useDispatch();
    const [openAllTagsDialog, setOpenAllTagsDialog] = useState(false);

    // Selectors
    const tags = useSelector(selectTags);
    const selectedTags = useSelector(selectSelectedTags);
    const tagMethodOr = useSelector(selectTagMethodOr);
    const displayTags = useSelector(selectDisplayTags);

    const handleTagClick = (tag) => {
        dispatch(toggleTag(tag.id));
    };

    const handleTagMethodChange = (val) => {
        if (val) {
            dispatch(setTagMethod(val === 'or'));
        }
    };

    return (
        <>
            <div className="flex items-center gap-2 px-4 py-1.5 border-t border-border/40 bg-muted/30">
                {/* Tag Logic Toggle */}
                <ToggleGroup
                    type="single"
                    value={tagMethodOr ? 'or' : 'and'}
                    onValueChange={handleTagMethodChange}
                    className="h-6"
                >
                    <ToggleGroupItem
                        value="or"
                        className="h-6 px-2 text-[10px] data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                    >
                        OR
                    </ToggleGroupItem>
                    <ToggleGroupItem
                        value="and"
                        className="h-6 px-2 text-[10px] data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                    >
                        AND
                    </ToggleGroupItem>
                </ToggleGroup>

                {/* Tags Horizontal Scroll */}
                <ScrollArea className="flex-1 whitespace-nowrap">
                    <div className="flex gap-1 py-0.5">
                        {displayTags.map((tag) => (
                            <Badge
                                key={tag.id}
                                variant={selectedTags.includes(tag.id) ? "default" : "outline"}
                                className={cn(
                                    "cursor-pointer h-6 text-[11px] font-normal transition-all hover:scale-105",
                                    selectedTags.includes(tag.id) 
                                        ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                                        : "hover:bg-accent"
                                )}
                                onClick={() => handleTagClick(tag)}
                            >
                                {tag.name}
                            </Badge>
                        ))}
                    </div>
                    <ScrollBar orientation="horizontal" className="h-1.5" />
                </ScrollArea>

                {/* More Tags Button */}
                <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs gap-1"
                    onClick={() => setOpenAllTagsDialog(true)}
                >
                    <Plus className="h-3 w-3" />
                    More
                </Button>
            </div>

            {/* All Tags Dialog */}
            <Dialog open={openAllTagsDialog} onOpenChange={setOpenAllTagsDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Tags className="h-5 w-5" />
                                Select Tags
                            </div>
                            <ToggleGroup
                                type="single"
                                value={tagMethodOr ? 'or' : 'and'}
                                onValueChange={handleTagMethodChange}
                                className="mr-8"
                            >
                                <ToggleGroupItem
                                    value="or"
                                    className="h-7 px-3 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                                >
                                    OR
                                </ToggleGroupItem>
                                <ToggleGroupItem
                                    value="and"
                                    className="h-7 px-3 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                                >
                                    AND
                                </ToggleGroupItem>
                            </ToggleGroup>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-muted-foreground mb-2">
                        {tagMethodOr 
                            ? "OR: Search for items with any of the selected tags" 
                            : "AND: Search for items with all of the selected tags"}
                    </div>
                    <ScrollArea className="h-[300px] pr-4">
                        <div className="flex flex-wrap gap-2">
                            {tags.map((tag) => (
                                <Badge
                                    key={tag.id}
                                    variant={selectedTags.includes(tag.id) ? "default" : "outline"}
                                    className={cn(
                                        "cursor-pointer h-7 text-xs font-normal transition-all hover:scale-105",
                                        selectedTags.includes(tag.id) 
                                            ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                                            : "hover:bg-accent"
                                    )}
                                    onClick={() => handleTagClick(tag)}
                                >
                                    {tag.name}
                                </Badge>
                            ))}
                        </div>
                    </ScrollArea>
                    <DialogFooter>
                        <Button onClick={() => setOpenAllTagsDialog(false)}>
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TagFilters;
